import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ArrowUpRight, Building2, ChevronRight, MapPin } from "lucide-react";
import { getEstablishmentsBySiren } from "../../interfaces/establishments";
import { IEstablishment } from "../../interfaces/establishment";
import { EstablishmentLogo } from "../../components/franchise/EstablishmentLogo";
import { Badge } from "../../components/ui/Badge";
import { displayName, fmtDate, fmtEuros, relativeFr, tradingStatus, TradingStatus } from "./detail/model";
import { CopyButton, Field, TONE_DOT } from "./detail/parts";

// Same look as <Button variant="secondary" size="sm">, for actions that are links.
const SECONDARY_LINK =
    "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-border-strong bg-transparent px-3 font-sans text-sm font-medium text-ink transition-colors duration-150 hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2";
const PRIMARY_LINK =
    "inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-transparent bg-ink px-3 font-sans text-sm font-medium text-ink-inverse transition-colors duration-150 hover:bg-ink/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2";

const SIGNAL_LABELS: Record<string, string> = {
    restaurant_opening: "Ouverture",
    restaurant_owner_change: "Changement de propriétaire",
    restaurant_closing: "Fermeture signalée",
    franchise_signing: "Signature franchise",
    network_expansion: "Expansion réseau",
    multi_site: "Multi-site",
    hygiene_control: "Contrôle sanitaire",
    bodacc_announcement: "Annonce BODACC",
};

type Filter = "all" | "open" | "closed";

interface Row {
    est: IEstablishment;
    status: TradingStatus;
    href: string;
}

const formatSiren = (s: string) => s.replace(/^(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3");
const formatSiret = (s: string) => s.replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, "$1 $2 $3 $4");

function latestSignal(e: IEstablishment) {
    return [...(e.signals || [])]
        .filter((s) => s.date)
        .sort((a, b) => +new Date(b.date!) - +new Date(a.date!))[0];
}

function StatusBadge({ status }: { status: TradingStatus }) {
    return (
        <Badge tone={status.tone}>
            <span className={`h-1.5 w-1.5 rounded-full ${TONE_DOT[status.tone]}`} />
            {status.label}
        </Badge>
    );
}

function Metric({ label, value }: { label: string; value: number }) {
    return (
        <div>
            <p className="font-sans text-2xl font-semibold tracking-tight text-ink">{value}</p>
            <p className="font-sans text-sm text-ink-muted">{label}</p>
        </div>
    );
}

function CompanyHeader({ siren, rows }: { siren: string; rows: Row[] }) {
    const navigate = useNavigate();
    const siege = rows.find((r) => r.est.registration?.is_siege)?.est;
    const ref = siege ?? rows[0]?.est;
    const reg = ref?.registration;
    const legalName = siege?.legal_name || rows.find((r) => r.est.legal_name)?.est.legal_name || (ref ? displayName(ref.name) : null);
    const open = rows.filter((r) => !r.status.closed).length;
    const cities = new Set(rows.map((r) => r.est.address?.city?.trim().toUpperCase()).filter(Boolean)).size;
    const siegeHref = rows.find((r) => r.est === siege)?.href;

    return (
        <header className="rounded-2xl border border-border bg-surface p-5 md:p-6">
            <button
                type="button"
                onClick={() => navigate(-1)}
                className="mb-4 inline-flex items-center gap-1.5 rounded-lg font-sans text-sm text-ink-muted transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
                <ArrowLeft className="h-4 w-4" />
                Retour
            </button>

            <div className="flex flex-col gap-4 md:flex-row md:items-start">
                <div className="flex min-w-0 flex-1 items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-border bg-surface-sunken">
                        <Building2 className="h-6 w-6 text-ink-muted" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-sans text-xs font-semibold uppercase tracking-wide text-ink-faint">Société</p>
                        <h1 className="mt-0.5 font-sans text-2xl font-semibold tracking-tight text-ink">
                            {legalName || "Société"}
                        </h1>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                            <span className="font-mono text-sm text-ink-muted">SIREN {formatSiren(siren)}</span>
                            <CopyButton value={siren} label="Copier le SIREN" />
                            {reg?.legal_form && <Badge tone="neutral">{reg.legal_form}</Badge>}
                            {reg?.company_size_category && <Badge tone="neutral">{reg.company_size_category}</Badge>}
                        </div>
                    </div>
                </div>

                <div className="flex shrink-0 flex-wrap gap-2">
                    <a href={`https://www.pappers.fr/entreprise/${siren}`} target="_blank" rel="noopener noreferrer" className={SECONDARY_LINK}>
                        Pappers
                        <ArrowUpRight className="h-4 w-4" />
                    </a>
                    {siegeHref && (
                        <Link to={siegeHref} className={PRIMARY_LINK}>
                            Fiche du siège
                            <ChevronRight className="h-4 w-4" />
                        </Link>
                    )}
                </div>
            </div>

            {reg && (
                <div className="mt-5 grid grid-cols-2 gap-4 border-t border-border pt-5 md:grid-cols-4">
                    <Field label="Activité" className="col-span-2">
                        {reg.naf_code || reg.naf_description ? (
                            <>
                                {reg.naf_code && <span className="font-mono">{reg.naf_code}</span>}
                                {reg.naf_code && reg.naf_description && " · "}
                                {reg.naf_description}
                            </>
                        ) : null}
                    </Field>
                    <Field label="Création">{fmtDate(reg.creation_date)}</Field>
                    <Field label="Capital">{reg.capital_eur ? fmtEuros(reg.capital_eur) : null}</Field>
                    <Field label="RCS">{reg.rcs_city}</Field>
                    <Field label="Effectif">{reg.workforce_range}</Field>
                    <Field label="TVA">{reg.vat_number && <span className="font-mono">{reg.vat_number}</span>}</Field>
                </div>
            )}

            {rows.length > 0 && (
                <div className="mt-5 grid grid-cols-2 gap-4 border-t border-border pt-5 md:grid-cols-4">
                    <Metric label={`établissement${rows.length > 1 ? "s" : ""}`} value={rows.length} />
                    <Metric label={`ouvert${open > 1 ? "s" : ""}`} value={open} />
                    <Metric label={`fermé${rows.length - open > 1 ? "s" : ""}`} value={rows.length - open} />
                    <Metric label={`ville${cities > 1 ? "s" : ""}`} value={cities} />
                </div>
            )}
        </header>
    );
}

function FilterToggle({ value, onChange, counts }: { value: Filter; onChange: (f: Filter) => void; counts: Record<Filter, number> }) {
    const options: Array<{ value: Filter; label: string }> = [
        { value: "all", label: "Tous" },
        { value: "open", label: "Ouverts" },
        { value: "closed", label: "Fermés" },
    ];
    return (
        <div role="tablist" className="inline-flex rounded-full bg-surface-sunken p-1">
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    role="tab"
                    aria-selected={value === o.value}
                    onClick={() => onChange(o.value)}
                    className={`rounded-full px-3 py-1 font-sans text-sm transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                        value === o.value ? "bg-surface text-ink shadow-card" : "text-ink-muted hover:text-ink"
                    }`}
                >
                    {o.label} <span className="text-ink-faint">{counts[o.value]}</span>
                </button>
            ))}
        </div>
    );
}

function EstablishmentRow({ row }: { row: Row }) {
    const { est: e, status } = row;
    const reg = e.registration;
    const activity = e.cuisine_types?.[0] || e.categories?.[0];
    const secondary = [reg?.enseigne && reg.enseigne !== e.name ? reg.enseigne : null, activity].filter(Boolean).join(" · ");
    const street = e.address?.street;
    const city = [e.address?.postcode, e.address?.city].filter(Boolean).join(" ");
    const signal = latestSignal(e);

    return (
        <Link
            to={row.href}
            className="group grid grid-cols-1 gap-3 border-t border-border px-4 py-4 transition-colors duration-150 first:border-t-0 hover:bg-surface-sunken/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent md:grid-cols-12 md:items-center md:gap-4"
        >
            <div className="flex min-w-0 items-center gap-3 md:col-span-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-surface-sunken">
                    <EstablishmentLogo name={e.name} franchise={(e as any).franchise} nafCode={reg?.naf_code} emojiClassName="text-lg" />
                </div>
                <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                        <span className={`truncate font-sans text-sm font-medium ${status.closed ? "text-ink-muted" : "text-ink"}`}>
                            {displayName(e.name)}
                        </span>
                        {reg?.is_siege && <Badge tone="accent" className="shrink-0">Siège</Badge>}
                    </div>
                    {secondary && <p className="truncate font-sans text-xs text-ink-muted">{secondary}</p>}
                </div>
            </div>

            <div className="flex min-w-0 items-start gap-2 md:col-span-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ink-faint md:hidden" />
                <div className="min-w-0 font-sans text-sm">
                    {street && <p className="truncate text-ink">{street}</p>}
                    {city && <p className="truncate text-ink-muted">{city}</p>}
                    {!street && !city && <p className="text-ink-faint">Adresse inconnue</p>}
                </div>
            </div>

            <div className="font-mono text-sm text-ink-muted md:col-span-2">
                {reg?.siret ? formatSiret(reg.siret) : <span className="font-sans text-ink-faint">—</span>}
            </div>

            <div className="flex items-center justify-between gap-2 md:col-span-2">
                <div className="min-w-0 space-y-1">
                    <StatusBadge status={status} />
                    {signal && (
                        <p className="truncate font-sans text-xs text-ink-faint" title={fmtDate(signal.date, "long") ?? undefined}>
                            {SIGNAL_LABELS[signal.type] ?? signal.type} · {relativeFr(new Date(signal.date!))}
                        </p>
                    )}
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-ink-faint transition-colors duration-150 group-hover:text-ink" />
            </div>
        </Link>
    );
}

export default function SirenEstablishments() {
    const { siren = "" } = useParams<{ siren: string }>();
    const [establishments, setEstablishments] = useState<IEstablishment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<Filter>("all");

    useEffect(() => {
        if (!siren) return;
        setLoading(true);
        setError(null);
        getEstablishmentsBySiren(siren)
            .then((res) => setEstablishments(res.establishments))
            .catch((err) => {
                console.error("Failed to load establishments for SIREN:", err);
                setError(
                    err?.response?.status === 400
                        ? "Ce SIREN n'est pas valide : il doit compter 9 chiffres."
                        : "Impossible de charger les établissements pour ce SIREN."
                );
            })
            .finally(() => setLoading(false));
    }, [siren]);

    // Siège first, then open sites, then the most recently created.
    const rows: Row[] = useMemo(
        () =>
            establishments
                .map((est) => ({
                    est,
                    status: tradingStatus(est),
                    href: `/establishments/${est.registration?.siret || String((est as any)._id)}`,
                }))
                .sort(
                    (a, b) =>
                        Number(!!b.est.registration?.is_siege) - Number(!!a.est.registration?.is_siege) ||
                        Number(a.status.closed) - Number(b.status.closed) ||
                        +new Date(b.est.registration?.creation_date || 0) - +new Date(a.est.registration?.creation_date || 0)
                ),
        [establishments]
    );

    const counts: Record<Filter, number> = {
        all: rows.length,
        open: rows.filter((r) => !r.status.closed).length,
        closed: rows.filter((r) => r.status.closed).length,
    };
    const visible = rows.filter((r) => filter === "all" || (filter === "open") === !r.status.closed);

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 md:px-8">
                {loading && (
                    <div className="flex justify-center py-16">
                        <div className="h-6 w-6 rounded-full border-2 border-ink border-t-transparent motion-safe:animate-spin" />
                    </div>
                )}

                {!loading && error && (
                    <div className="flex items-center gap-2 rounded-2xl border border-danger/20 bg-danger-subtle px-4 py-3 text-sm text-danger">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        {error}
                    </div>
                )}

                {!loading && !error && (
                    <>
                        <CompanyHeader siren={siren} rows={rows} />

                        {rows.length === 0 ? (
                            <div className="rounded-2xl border border-border bg-surface px-6 py-16 text-center">
                                <Building2 className="mx-auto mb-3 h-8 w-8 text-ink-faint" />
                                <p className="font-display text-xl text-ink">Aucun établissement</p>
                                <p className="mt-1 text-sm text-ink-muted">Nous n'avons aucun établissement rattaché à ce SIREN.</p>
                            </div>
                        ) : (
                            <section className="overflow-hidden rounded-2xl border border-border bg-surface">
                                <div className="flex flex-wrap items-center justify-between gap-3 p-4 md:px-6">
                                    <h2 className="font-sans text-base font-semibold text-ink">Établissements</h2>
                                    {counts.closed > 0 && counts.open > 0 && (
                                        <FilterToggle value={filter} onChange={setFilter} counts={counts} />
                                    )}
                                </div>
                                <div className="hidden grid-cols-12 gap-4 border-t border-border bg-surface-sunken px-4 py-2 text-xs font-semibold uppercase tracking-wide text-ink-faint md:grid">
                                    <span className="col-span-5">Établissement</span>
                                    <span className="col-span-3">Adresse</span>
                                    <span className="col-span-2">SIRET</span>
                                    <span className="col-span-2">Statut</span>
                                </div>
                                <div className="border-t border-border md:border-t-0">
                                    {visible.map((row) => (
                                        <EstablishmentRow key={String((row.est as any)._id)} row={row} />
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
